import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppHeader from "@/components/feature/AppHeader";
import AppFooter from "@/components/feature/AppFooter";
import CodePoolPanel from "@/pages/admin/tools/components/CodePoolPanel";
import ExportAuditTrail from "@/pages/admin/tools/components/ExportAuditTrail";
import OfflineGenerator from "@/pages/admin/tools/components/OfflineGenerator";
import { useLicenseTools } from "@/pages/admin/tools/hooks/useLicenseTools";

type Tab = "pool" | "offline";

interface Notice {
  text: string;
  tone: "success" | "error";
}

export default function LicenseToolsPage() {
  const { t } = useTranslation();
  const {
    codes,
    exportLogs,
    products,
    loading,
    error,
    reload,
    generateCodes,
    logCodeExport,
    setCodeStatus,
    deleteCodes,
    getOfflinePublicKey,
    generateOfflineCode,
  } = useLicenseTools();
  const [tab, setTab] = useState<Tab>("pool");
  const [notice, setNotice] = useState<Notice | null>(null);

  const notify = (text: string, tone: "success" | "error" = "success") => {
    setNotice({ text, tone });
    window.setTimeout(() => setNotice(null), 4000);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <AppHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="cursor-pointer transition-colors hover:text-foreground-950">
                {t("tools.breadcrumbHome")}
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <Link to="/admin" className="cursor-pointer transition-colors hover:text-foreground-950">
                {t("tools.breadcrumbAdmin")}
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <span className="text-foreground-800">{t("tools.title")}</span>
            </nav>
            <div className="mt-4 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-700">
                  <span className="flex h-3 w-3 items-center justify-center">
                    <i className="ri-tools-line" />
                  </span>
                  {t("tools.badge")}
                </span>
                <h1 className="mt-3 font-heading text-2xl font-semibold tracking-tight text-foreground-950 md:text-4xl">
                  {t("tools.title")}
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  {t("tools.subtitle")}
                </p>
              </div>
              <Link
                to="/docs/integration"
                className="inline-flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100 md:w-auto"
              >
                <span className="flex h-4 w-4 items-center justify-center text-primary-700">
                  <i className="ri-book-open-line" />
                </span>
                {t("tools.docs")}
              </Link>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
          {error ? (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-16 text-center">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-error-warning-line text-xl" />
              </span>
              <p className="text-sm text-foreground-600">{error}</p>
              <button
                type="button"
                onClick={reload}
                className="cursor-pointer rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                {t("tools.reload")}
              </button>
            </div>
          ) : (
            <>
              <div className="mb-6 inline-flex rounded-full border border-background-300 bg-background-100 p-1">
                <button
                  type="button"
                  onClick={() => setTab("pool")}
                  className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    tab === "pool" ? "bg-background-50 text-foreground-950" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-stack-line" />
                  </span>
                  {t("tools.tabPool")}
                </button>
                <button
                  type="button"
                  onClick={() => setTab("offline")}
                  className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    tab === "offline" ? "bg-background-50 text-foreground-950" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-fingerprint-line" />
                  </span>
                  {t("tools.tabOffline")}
                </button>
              </div>

              {tab === "pool" ? (
                <>
                  <CodePoolPanel
                    codes={codes}
                    exportLogs={exportLogs}
                    products={products}
                    loading={loading}
                    notify={notify}
                    onGenerate={generateCodes}
                    onLogExport={logCodeExport}
                    onSetStatus={setCodeStatus}
                    onDelete={deleteCodes}
                  />
                  <div className="mt-6">
                    <ExportAuditTrail logs={exportLogs} codes={codes} />
                  </div>
                </>
              ) : (
                <OfflineGenerator
                  notify={notify}
                  onGenerate={generateOfflineCode}
                  onRevealPublicKey={getOfflinePublicKey}
                />
              )}
            </>
          )}
        </section>
      </main>

      {notice && (
        <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 px-4">
          <div
            className={`lh-fade-up flex items-center gap-2.5 rounded-lg px-4 py-3 text-sm shadow-sm ${
              notice.tone === "success" ? "bg-primary-600 text-background-50" : "bg-accent-600 text-background-50"
            }`}
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className={notice.tone === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} />
            </span>
            {notice.text}
          </div>
        </div>
      )}

      <AppFooter />
    </div>
  );
}